import {
  normalizeAppEntrySource,
  type AppEntrySource,
} from "./share-entry";
import {
  normalizeSourceLanguageBucket,
  type SourceLanguageBucket,
} from "./language";

export const ACTIVITY_COUNTER_KEYS = [
  "productOpens",
  "catalogOpens",
  "searches",
  "shoppingAdds",
  "shoppingRemoves",
  "shoppingListShares",
  "productShares",
  "catalogShares",
  "appShares",
  "storeSavePromptEligible",
  "storeSavePromptTreatment",
  "storeSavePromptShown",
  "storeSavePromptAccepted",
  "storeSavePromptDismissed",
  "homeScreenPromptEligible",
  "homeScreenPromptTreatment",
  "homeScreenPromptShown",
  "homeScreenPromptRequested",
  "homeScreenPromptCompleted",
  "homeScreenPromptDismissed",
  "homeScreenPromptUnsupported",
  "homeScreenPromptAlreadyAdded",
  "homeScreenPromptFailed",
  "starterShelfEligible",
  "starterShelfTreatment",
  "starterShelfShown",
  "starterShelfGroupOpens",
  "starterShelfProductOpens",
  "feedProductOpens",
  "searchProductOpens",
  "catalogProductOpens",
  "relatedProductOpens",
  "zeroResultSearches",
  "mealSectionOpens",
  "mealRecipeOpens",
  "mealStoreOpens",
  "mealProductOpens",
  "mealListAdds",
  "comparisonSearches",
  "comparisonAllOpens",
  "fuelSectionOpens",
  "fuelStationOpens",
  "fuelRouteOpens",
  "fuelNearbyUses",
  "fuelFavoriteAdds",
  "fuelAlertEnables",
] as const;

export type ActivityCounterKey = (typeof ACTIVITY_COUNTER_KEYS)[number];
export type ActivityCounters = Record<ActivityCounterKey, number>;
export type ActivityColorScheme = "light" | "dark";

export interface ActivityBatch {
  batchId: string;
  appOpen: boolean;
  entrySource: AppEntrySource | null;
  colorScheme: ActivityColorScheme | null;
  browserLanguageBucket?: SourceLanguageBucket | null;
  counters: ActivityCounters;
}

const ACTIVITY_BATCH_ID_PATTERN = /^[a-zA-Z0-9_-]{16,64}$/;
const MAX_COUNTER_PER_BATCH = 10_000;

export function emptyActivityCounters(): ActivityCounters {
  return {
    productOpens: 0,
    catalogOpens: 0,
    searches: 0,
    shoppingAdds: 0,
    shoppingRemoves: 0,
    shoppingListShares: 0,
    productShares: 0,
    catalogShares: 0,
    appShares: 0,
    storeSavePromptEligible: 0,
    storeSavePromptTreatment: 0,
    storeSavePromptShown: 0,
    storeSavePromptAccepted: 0,
    storeSavePromptDismissed: 0,
    homeScreenPromptEligible: 0,
    homeScreenPromptTreatment: 0,
    homeScreenPromptShown: 0,
    homeScreenPromptRequested: 0,
    homeScreenPromptCompleted: 0,
    homeScreenPromptDismissed: 0,
    homeScreenPromptUnsupported: 0,
    homeScreenPromptAlreadyAdded: 0,
    homeScreenPromptFailed: 0,
    starterShelfEligible: 0,
    starterShelfTreatment: 0,
    starterShelfShown: 0,
    starterShelfGroupOpens: 0,
    starterShelfProductOpens: 0,
    feedProductOpens: 0,
    searchProductOpens: 0,
    catalogProductOpens: 0,
    relatedProductOpens: 0,
    zeroResultSearches: 0,
    mealSectionOpens: 0,
    mealRecipeOpens: 0,
    mealStoreOpens: 0,
    mealProductOpens: 0,
    mealListAdds: 0,
    comparisonSearches: 0,
    comparisonAllOpens: 0,
    fuelSectionOpens: 0,
    fuelStationOpens: 0,
    fuelRouteOpens: 0,
    fuelNearbyUses: 0,
    fuelFavoriteAdds: 0,
    fuelAlertEnables: 0,
  };
}

function defaultBatchId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}

function normalizeColorScheme(value: unknown): ActivityColorScheme | null {
  return value === "light" || value === "dark" ? value : null;
}

function createBatch(
  batchId: string,
  appOpen: boolean,
  entrySource: AppEntrySource | null,
  colorScheme: ActivityColorScheme | null,
  browserLanguageBucket: SourceLanguageBucket | null = null,
): ActivityBatch {
  return {
    batchId,
    appOpen,
    entrySource: appOpen ? normalizeAppEntrySource(entrySource) : null,
    colorScheme,
    ...(browserLanguageBucket ? { browserLanguageBucket } : {}),
    counters: emptyActivityCounters(),
  };
}

function hasCounters(batch: ActivityBatch): boolean {
  return ACTIVITY_COUNTER_KEYS.some((key) => batch.counters[key] > 0);
}

export function normalizeActivityBatch(value: unknown): ActivityBatch | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Partial<ActivityBatch>;
  if (
    typeof candidate.batchId !== "string" ||
    !ACTIVITY_BATCH_ID_PATTERN.test(candidate.batchId) ||
    typeof candidate.appOpen !== "boolean" ||
    !candidate.counters ||
    typeof candidate.counters !== "object"
  ) {
    return null;
  }
  const counters = emptyActivityCounters();
  for (const key of ACTIVITY_COUNTER_KEYS) {
    const count = (candidate.counters as Partial<ActivityCounters>)[key];
    if (count === undefined) {
      counters[key] = 0;
      continue;
    }
    if (
      typeof count !== "number" ||
      !Number.isInteger(count) ||
      count < 0 ||
      count > MAX_COUNTER_PER_BATCH
    ) {
      return null;
    }
    counters[key] = count;
  }
  const browserLanguageBucket = normalizeSourceLanguageBucket(
    candidate.browserLanguageBucket,
  );
  const batch: ActivityBatch = {
    batchId: candidate.batchId,
    appOpen: candidate.appOpen,
    entrySource: candidate.appOpen
      ? normalizeAppEntrySource(candidate.entrySource) ?? "direct"
      : null,
    colorScheme: normalizeColorScheme(candidate.colorScheme),
    ...(browserLanguageBucket ? { browserLanguageBucket } : {}),
    counters,
  };
  return batch.appOpen || hasCounters(batch) ? batch : null;
}

export class ActivityAccumulator {
  private readonly queue: ActivityBatch[];
  private colorScheme: ActivityColorScheme | null;

  constructor(
    private readonly batchIdFactory: () => string = defaultBatchId,
    colorScheme: ActivityColorScheme | null = null,
    entrySource: AppEntrySource = "direct",
    browserLanguageBucket: SourceLanguageBucket | null = null,
  ) {
    this.colorScheme = normalizeColorScheme(colorScheme);
    this.queue = [
      createBatch(
        this.batchIdFactory(),
        true,
        normalizeAppEntrySource(entrySource) ?? "direct",
        this.colorScheme,
        normalizeSourceLanguageBucket(browserLanguageBucket),
      ),
    ];
  }

  setColorScheme(colorScheme: ActivityColorScheme | null): void {
    this.colorScheme = normalizeColorScheme(colorScheme);
  }

  record(key: ActivityCounterKey, amount = 1): void {
    if (!Number.isFinite(amount) || amount <= 0) {
      return;
    }
    let batch = this.queue.at(-1);
    if (!batch) {
      batch = createBatch(
        this.batchIdFactory(),
        false,
        null,
        this.colorScheme,
        null,
      );
      this.queue.push(batch);
    }
    batch.counters[key] = Math.min(
      MAX_COUNTER_PER_BATCH,
      batch.counters[key] + Math.trunc(amount),
    );
  }

  take(): ActivityBatch | undefined {
    return this.queue.shift();
  }

  restore(batch: ActivityBatch): void {
    this.queue.unshift(batch);
  }

  hasPending(): boolean {
    return this.queue.length > 0;
  }
}
